import { useMemo, useState } from "react";
import { useList } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, CircleDot, Send, ShieldAlert } from "lucide-react";

const LABELS = {
  trial_confirmed: "Trial confirmed",
  enrolment_confirmed: "Enrolled",
  claim_ready: "Ready to invite",
  claim_invited: "Invitation sent",
  account_claimed: "Account claimed",
  complete: "Complete",
  needs_review: "Needs review",
};

export const OnboardingJourneyPanel = ({ onInvite }) => {
  const [stage, setStage] = useState("");
  const { data, loading, reload } = useList(
    `/admin/onboarding/journeys${stage ? `?stage=${stage}` : ""}`, [stage]);
  const rows = useMemo(() => data?.rows || [], [data]);
  const totals = useMemo(() => ({
    active: rows.filter((r) => !["complete", "needs_review"].includes(r.stage)).length,
    invite: rows.filter((r) => r.next_action === "send_claim").length,
    waiting: rows.filter((r) => r.next_action === "await_claim").length,
    complete: rows.filter((r) => r.stage === "complete").length,
  }), [rows]);

  return (
    <section className="mb-8" data-testid="onboarding-journey-panel">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
        <div><p className="text-xs font-semibold uppercase text-muted-foreground">Live family pipeline</p>
          <h2 className="font-display text-2xl font-semibold mt-1">Onboarding journeys</h2></div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant={!stage ? "default" : "outline"} onClick={() => setStage("")} data-testid="onboarding-filter-all">All</Button>
          {["trial_confirmed", "claim_ready", "claim_invited", "complete"].map((item) => <Button key={item} size="sm" variant={stage === item ? "default" : "outline"} onClick={() => setStage(item)} data-testid={`onboarding-filter-${item}`}>{LABELS[item]}</Button>)}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[["Active", totals.active], ["Ready to invite", totals.invite], ["Awaiting claim", totals.waiting], ["Complete", totals.complete]].map(([label, value]) => <div key={label} className="bg-white border rounded-md p-4" data-testid={`onboarding-kpi-${label.toLowerCase().replaceAll(" ", "-")}`}><p className="text-xs text-muted-foreground">{label}</p><p className="font-display text-2xl font-semibold mt-1">{value}</p></div>)}
      </div>
      <div className="border rounded-lg bg-white overflow-hidden" data-testid="onboarding-journey-list">
        {loading ? <p className="p-6 text-sm text-muted-foreground">Loading journeys…</p> : !rows.length ? <p className="p-6 text-sm text-muted-foreground" data-testid="onboarding-empty-state">No onboarding journeys yet.</p> : rows.map((row) => (
          <div key={row.id} className="grid md:grid-cols-[1.3fr_1fr_auto] gap-4 p-4 border-b last:border-0 items-center" data-testid={`onboarding-journey-${row.id}`}>
            <div className="min-w-0"><div className="flex items-center gap-2">{row.stage === "complete" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : row.stage === "needs_review" ? <ShieldAlert className="h-4 w-4 text-amber-600" /> : <CircleDot className="h-4 w-4 text-primary" />}<p className="font-semibold truncate">{row.student?.full_name || "Student"}</p><Badge variant="outline" data-testid={`onboarding-stage-${row.id}`}>{LABELS[row.stage] || row.stage}</Badge></div><p className="text-sm text-muted-foreground mt-1">{row.parent?.full_name || "Family"} · {row.source?.replaceAll("_", " ")}</p></div>
            <div><p className="text-xs font-semibold uppercase text-muted-foreground">Next action</p><p className="text-sm mt-1">{row.next_action?.replaceAll("_", " ")}</p><p className="text-xs text-muted-foreground">Recommended: {row.recommended_channel?.replaceAll("_", " ")}</p></div>
            {row.next_action === "send_claim" && row.parent && <Button size="sm" onClick={async () => { await onInvite(row.parent, row.recommended_channel === "whatsapp" ? "whatsapp" : "email"); reload(); }} data-testid={`onboarding-send-claim-${row.id}`}><Send className="h-4 w-4 mr-1" /> Invite</Button>}
          </div>
        ))}
      </div>
    </section>
  );
};